import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";
import type { Product } from "../../../components/products/ProductInterface";
import { getProductsPage } from "../../../services/products/productAxios";

export const ALL_PRODUCTS_OPTION = "__all_products__";

const PAGE_SIZE = 20;

const productThumbnail = (product: Product) =>
  (product as Product & { base_image_url?: string | null; best_image_url?: string | null }).base_image_url ||
  (product as Product & { best_image_url?: string | null }).best_image_url ||
  product.images?.[0] ||
  null;

const productShortCode = (product: Product) =>
  (product as Product & { short_code?: string | null }).short_code || null;

type ProductPickerSelectProps = {
  placeholder?: string;
  disabled?: boolean;
  excludeIds?: string[];
  // Productos que NO se pueden elegir porque ya están en otra acción del mismo tipo:
  // se muestran gray-out (no ocultos) con el nombre de la otra acción (Julian).
  disabledReasonById?: Record<string, string>;
  includeAllOption?: boolean;
  allOptionLabel?: string;
  onSelect: (productId: string, product?: Product) => void;
};

export const ProductPickerSelect = ({
  placeholder = "Seleccionar producto",
  disabled = false,
  excludeIds = [],
  disabledReasonById = {},
  includeAllOption = false,
  allOptionLabel = "Todos los productos",
  onSelect,
}: ProductPickerSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const excluded = useMemo(() => new Set(excludeIds.map(String)), [excludeIds]);

  const loadPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const response = await getProductsPage({
          page: nextPage,
          size: PAGE_SIZE,
          search: debouncedSearch || undefined,
        });
        const items = (response.items || []).filter((product) => !excluded.has(String(product.id)));
        setProducts((current) => (append ? [...current, ...items] : items));
        setPage(response.page ?? nextPage);
        setTotalPages(response.total_pages ?? 0);
      } catch (error) {
        console.warn("[ProductPickerSelect] load failed", error);
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [debouncedSearch, excluded]
  );

  useEffect(() => {
    if (!open) return;
    void loadPage(0, false);
  }, [open, loadPage]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const canLoadMore = page + 1 < totalPages;

  const handleSelect = (value: string, product?: Product) => {
    onSelect(value, product);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="truncate text-gray-500">{placeholder}</span>
        <Search className="size-4 shrink-0 text-gray-400" />
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-11 z-40 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_8px_24px_rgba(15,23,42,0.12)]">
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar producto"
                className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-[#4368E5] focus:ring-2 focus:ring-[#4368E5]/10"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {includeAllOption ? (
              <button
                type="button"
                onClick={() => handleSelect(ALL_PRODUCTS_OPTION)}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-gray-50"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-gray-100 text-xs font-semibold text-gray-600">
                  ALL
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-gray-950">{allOptionLabel}</span>
                  <span className="block text-xs text-gray-500">Aplica a cualquier producto</span>
                </span>
              </button>
            ) : null}
            {loading ? (
              <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-gray-500">
                <Loader2 className="size-4 animate-spin" />
                Cargando productos...
              </div>
            ) : products.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-gray-500">No se encontraron productos.</div>
            ) : (
              products.map((product) => {
                const thumbnail = productThumbnail(product);
                const shortCode = productShortCode(product);
                const usedInActionName = disabledReasonById[String(product.id)];
                const optionDisabled = Boolean(usedInActionName);
                return (
                  <button
                    key={product.id}
                    type="button"
                    disabled={optionDisabled}
                    onClick={() => handleSelect(String(product.id), product)}
                    title={optionDisabled ? `Ya está en "${usedInActionName}"` : undefined}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition ${
                      optionDisabled ? "cursor-not-allowed opacity-50" : "hover:bg-gray-50"
                    }`}
                  >
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt=""
                        className={`size-10 shrink-0 rounded-md object-cover ${optionDisabled ? "grayscale" : ""}`}
                      />
                    ) : (
                      <span className="size-10 shrink-0 rounded-md bg-gray-100" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block truncate text-sm font-semibold ${
                          optionDisabled ? "text-gray-500" : "text-gray-950"
                        }`}
                      >
                        {product.name}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        {shortCode ? <span className="font-mono">{shortCode}</span> : null}
                        <span>{product.price_formatted || product.price}</span>
                      </span>
                      {optionDisabled ? (
                        <span className="mt-0.5 block truncate text-xs text-gray-400">
                          Ya está en "{usedInActionName}"
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })
            )}
            {canLoadMore ? (
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => void loadPage(page + 1, true)}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
              >
                {loadingMore ? <Loader2 className="size-4 animate-spin" /> : null}
                Cargar más
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const productPickerLabel = (product: Product) => product.name;

export const productPickerChipContent = (product: Product) => {
  const thumbnail = productThumbnail(product);
  const shortCode = productShortCode(product);
  return { thumbnail, shortCode, name: product.name };
};
